import { memo, useCallback, useRef, useState, type CSSProperties, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  GripVertical,
  Info,
  MoreHorizontal,
  Pencil,
  RectangleHorizontal,
  RectangleVertical,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { sourceLabel, widgetSourceIds } from '@/features/intelligence/dashboard/catalog';
import { clampH, clampW, HEIGHT_PRESETS, WIDTH_PRESETS } from '@/features/intelligence/dashboard/layout';
import { GRID_COLUMNS, GRID_GAP_PX, GRID_ROW_PX, type DashboardWidget } from '@/features/intelligence/dashboard/types';
import { useDashboard } from './DashboardContext';
import { ChartWidgetView } from './widgets/ChartWidgetView';
import { ForecastWidgetView } from './widgets/ForecastWidgetView';
import { MetricWidgetView } from './widgets/MetricWidgetView';
import { TableWidgetView } from './widgets/TableWidgetView';
import { TextWidgetView } from './widgets/TextWidgetView';

export interface GridMetrics {
  colWidth: number;
  rowHeight: number;
  gap: number;
}

export interface WidgetFrameHandlers {
  onEdit: (widgetId: string) => void;
  onDuplicate: (widgetId: string) => void;
  onRemove: (widgetId: string) => void;
  onNudge: (widgetId: string, direction: -1 | 1) => void;
  onResize: (widgetId: string, w: number, h: number) => void;
  onDragStart: (widgetId: string, sectionId: string) => void;
  onDragEnd: () => void;
  onDragOverWidget: (event: DragEvent, sectionId: string, index: number) => void;
  onDrop: (event: DragEvent) => void;
}

interface WidgetFrameProps extends WidgetFrameHandlers {
  widget: DashboardWidget;
  sectionId: string;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  dropIndicator: 'before' | 'after' | null;
  isDragging: boolean;
  getGridMetrics: () => GridMetrics | null;
}

export function WidgetBody({ widget }: { widget: DashboardWidget }) {
  switch (widget.kind) {
    case 'metric':
      return <MetricWidgetView widget={widget} />;
    case 'chart':
      return <ChartWidgetView widget={widget} />;
    case 'forecast':
      return <ForecastWidgetView widget={widget} />;
    case 'table':
      return <TableWidgetView widget={widget} />;
    case 'text':
      return <TextWidgetView widget={widget} />;
  }
}

function WidgetFrameImpl({
  widget,
  sectionId,
  index,
  isFirst,
  isLast,
  dropIndicator,
  isDragging,
  getGridMetrics,
  onEdit,
  onDuplicate,
  onRemove,
  onNudge,
  onResize,
  onDragStart,
  onDragEnd,
  onDragOverWidget,
  onDrop,
}: WidgetFrameProps) {
  const { catalog, editing, isMobile } = useDashboard();
  const [preview, setPreview] = useState<{ w: number; h: number } | null>(null);
  const resizeStart = useRef<{ x: number; y: number; w: number; h: number; metrics: GridMetrics } | null>(null);

  const sourceIds = widgetSourceIds(widget);
  const primary = sourceIds[0] ? catalog.byId.get(sourceIds[0]) : undefined;
  const title = widget.title ?? (sourceIds[0] ? sourceLabel(sourceIds[0], catalog) : widget.kind === 'text' ? '' : 'Widget');
  const description = primary?.description;

  const w = preview?.w ?? widget.w;
  const h = preview?.h ?? widget.h;
  const style: CSSProperties = isMobile
    ? { gridColumn: '1 / -1', gridRow: `span ${Math.max(1, Math.min(h, widget.kind === 'metric' ? 2 : h))}` }
    : { gridColumn: `span ${w} / span ${w}`, gridRow: `span ${h} / span ${h}` };

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const metrics = getGridMetrics();
      if (!metrics) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      resizeStart.current = { x: event.clientX, y: event.clientY, w: widget.w, h: widget.h, metrics };
      setPreview({ w: widget.w, h: widget.h });
    },
    [getGridMetrics, widget.w, widget.h],
  );

  const onResizePointerMove = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = resizeStart.current;
    if (!start) return;
    const { colWidth, rowHeight, gap } = start.metrics;
    const dw = Math.round((event.clientX - start.x) / (colWidth + gap));
    const dh = Math.round((event.clientY - start.y) / (rowHeight + gap));
    setPreview({ w: clampW(start.w + dw), h: clampH(start.h + dh) });
  }, []);

  const onResizePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const start = resizeStart.current;
      resizeStart.current = null;
      event.currentTarget.releasePointerCapture(event.pointerId);
      setPreview((p) => {
        if (p && start && (p.w !== start.w || p.h !== start.h)) onResize(widget.id, p.w, p.h);
        return null;
      });
    },
    [onResize, widget.id],
  );

  return (
    <div
      style={style}
      data-widget-id={widget.id}
      draggable={editing && !isMobile}
      onDragStart={(e) => {
        if (!editing) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', widget.id);
        onDragStart(widget.id, sectionId);
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => editing && onDragOverWidget(e, sectionId, index)}
      onDrop={onDrop}
      onDoubleClick={editing ? () => onEdit(widget.id) : undefined}
      onKeyDown={
        editing
          ? (e) => {
              if (e.key === 'Enter' && e.target === e.currentTarget) onEdit(widget.id);
            }
          : undefined
      }
      tabIndex={editing ? 0 : undefined}
      aria-label={editing ? `${title || 'Widget'} — press Enter to edit` : undefined}
      className={cn(
        'group relative flex min-h-0 min-w-0 flex-col rounded-lg border border-border bg-card text-card-foreground shadow-[0_1px_2px_0_hsl(124_26%_14%/0.04)] transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        editing && 'cursor-grab select-none ring-offset-background hover:shadow-md active:cursor-grabbing',
        isDragging && 'opacity-40',
        preview && 'ring-2 ring-atmosphere-dark/60',
      )}
    >
      {dropIndicator && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-1 bottom-1 z-10 w-1 rounded-full bg-atmosphere-dark',
            dropIndicator === 'before' ? '-left-[10px]' : '-right-[10px]',
          )}
        />
      )}

      <div className={cn('flex shrink-0 items-center gap-1.5 px-4 pt-3', title ? 'pb-1' : 'pb-0')}>
        {editing && !isMobile && <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/70" strokeWidth={1.8} />}
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={title}>
          {title}
        </h3>
        {!editing && description && (
          <span className="hidden text-muted-foreground/60 group-hover:inline" title={description} aria-label={description}>
            <Info className="h-3.5 w-3.5" strokeWidth={1.8} />
          </span>
        )}
        {editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="-mr-2 h-7 w-7" aria-label="Widget options" onPointerDown={(e) => e.stopPropagation()}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onSelect={() => onEdit(widget.id)}>
                <Pencil className="mr-2 h-4 w-4" /> Edit widget
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onDuplicate(widget.id)}>
                <Copy className="mr-2 h-4 w-4" /> Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={isFirst} onSelect={() => onNudge(widget.id, -1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Move earlier
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isLast} onSelect={() => onNudge(widget.id, 1)}>
                <ArrowRight className="mr-2 h-4 w-4" /> Move later
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <RectangleHorizontal className="mr-2 h-4 w-4" /> Width
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {WIDTH_PRESETS.map((p) => (
                    <DropdownMenuItem key={p.w} onSelect={() => onResize(widget.id, p.w, widget.h)} className={cn(widget.w === p.w && 'font-medium')}>
                      {p.label}
                      <span className="ml-auto text-xs text-muted-foreground">{p.w}/{GRID_COLUMNS}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <RectangleVertical className="mr-2 h-4 w-4" /> Height
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {HEIGHT_PRESETS.map((p) => (
                    <DropdownMenuItem key={p.h} onSelect={() => onResize(widget.id, widget.w, p.h)} className={cn(widget.h === p.h && 'font-medium')}>
                      {p.label}
                      <span className="ml-auto text-xs text-muted-foreground">{p.h * GRID_ROW_PX + (p.h - 1) * GRID_GAP_PX}px</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-fresh-salmon-dark focus:text-fresh-salmon-dark" onSelect={() => onRemove(widget.id)}>
                <Trash2 className="mr-2 h-4 w-4" /> Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className={cn('min-h-0 flex-1 px-4 pb-3', editing && 'pointer-events-none')}>
        <WidgetBody widget={widget} />
      </div>

      {editing && !isMobile && (
        <button
          type="button"
          aria-label="Resize widget"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
          className="absolute bottom-1 right-1 z-10 h-5 w-5 cursor-nwse-resize rounded-sm text-muted-foreground/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden>
            <circle cx="15" cy="15" r="1.4" />
            <circle cx="10" cy="15" r="1.4" />
            <circle cx="15" cy="10" r="1.4" />
          </svg>
        </button>
      )}

    </div>
  );
}

export const WidgetFrame = memo(WidgetFrameImpl);
